/**
 * Read-only attendance row for a soft-deleted participant.
 */

import * as React from 'react';
import { Avatar } from './Avatar';
import { ATTENDANCE_STATUS_LABELS } from '@/constants/shared-catalog';
import type { AttendanceStatus } from '@/types/attendance.types';
import type { SessionParticipant } from '@/types/inperson/session.types';
import { getRoleLabel } from '@/utils/role-label-utils';

/** Fallback label when the archived participant name is missing. */
const UNKNOWN_PARTICIPANT_LABEL = 'Participant inconnu';
/** Hint that attendance cannot be changed on a deleted account. */
const ARCHIVED_HINT = 'Compte supprimé — présence non modifiable';

/** Props for {@link ArchivedAttendanceRow}. */
interface ArchivedAttendanceRowProps {
  participant: SessionParticipant;
}

/** Theme color and French role label per participant role. */
const roleColors = {
  tutor: 'var(--color-2-nuance-1)',
  student: 'var(--color-1-nuance-1)',
  volunteer: 'var(--color-3-nuance-1)',
};

/** Role labels resolved from the live role catalog. */
const roleLabels = {
  get tutor() {
    return getRoleLabel('tutor');
  },
  get student() {
    return getRoleLabel('student');
  },
  get volunteer() {
    return getRoleLabel('volunteer');
  },
};

/** Read-only attendance row for an archived session participant. */
export const ArchivedAttendanceRow: React.FC<ArchivedAttendanceRowProps> = ({ participant }) => {
  const status = (participant.attendanceStatus || 'pending') as AttendanceStatus;
  const statusLabel = ATTENDANCE_STATUS_LABELS[status] ?? status;

  return (
    <div
      className="space-y-2 p-4 rounded-lg border-2 bg-[color-mix(in_srgb,var(--color-1-nuance-3)_5%,transparent)] opacity-90"
      style={{ borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 30%, transparent)' }}
    >
      <div className="flex items-center gap-3">
        <Avatar src={null} fallback="?" size="sm" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground text-sm">{UNKNOWN_PARTICIPANT_LABEL}</p>
          <p
            className="text-xs text-muted-foreground"
            style={{ color: roleColors[participant.role] }}
          >
            {roleLabels[participant.role]}
          </p>
        </div>
        <span className="text-xs font-medium text-muted-foreground shrink-0">{statusLabel}</span>
      </div>
      <p className="text-xs text-muted-foreground italic">{ARCHIVED_HINT}</p>
      {participant.absenceJustification && status === 'absent' && (
        <p className="text-xs text-muted-foreground">
          Motif enregistré&nbsp;: {participant.absenceJustification}
        </p>
      )}
    </div>
  );
};
